import { GameMode, GameVariation } from '../types';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Tooltip } from './ui/Tooltip';

interface GameVariantSelectorProps {
  gameVariation: GameVariation;
  setGameVariation: (variation: GameVariation) => void;
  /** When 'local', the online-only Blind Chess960 variant is disabled. */
  gameMode?: GameMode;
}

type VariantGroup = 'standard' | 'chess960' | 'custom' | 'blind';

const variantGroups: Record<VariantGroup, { label: string; description: string; variations: GameVariation[] }> = {
  standard: {
    label: 'Standard Chess',
    description: 'Traditional chess with standard starting position. Queens only, limited promotion (queen, rook, bishop, knight).',
    variations: ['chess', 'chessx2']
  },
  chess960: {
    label: 'Chess960',
    description: 'Fischer Random chess with 960 possible starting positions. Queens only, Chess960 castling rules.',
    variations: ['chess960', 'chess960x2']
  },
  custom: {
    label: 'Custom Major Pieces',
    description: 'Replace queens with Hawk (knight+bishop), Elephant (knight+rook), or Amazon (knight+rook+bishop).',
    variations: ['chessx8', 'chessx32', 'chess960x8', 'chess960x32']
  },
  blind: {
    label: 'Blind Chess960',
    description: 'Online-only. Each player secretly arranges their own back rank — the opponent\'s setup stays hidden until the game starts.',
    variations: ['chess960blind']
  }
};

const variationLabels: Record<GameVariation, string> = {
  chess: 'Standard Chess',
  chessx2: 'Standard Chess x2',
  chessx8: 'Standard Chess x8',
  chessx32: 'Standard Chess x32',
  chess960: 'Chess960',
  chess960x2: 'Chess960 x2',
  chess960x8: 'Chess960 x8',
  chess960x32: 'Chess960 x32',
  chess960blind: 'Blind Chess960'
};

const variationDescriptions: Record<GameVariation, string> = {
  chess: 'Standard chess starting position. Queens only. Limited promotion (queen, rook, bishop, knight). 1 configuration.',
  chessx2: 'Standard chess starting position. Queens only. Full promotion (queen, rook, bishop, knight, hawk, elephant, amazon). 2 configurations.',
  chessx8: 'Standard chess starting position. Both players use same major piece (Queen/Hawk/Elephant/Amazon). Limited or full promotion. 8 configurations.',
  chessx32: 'Standard chess starting position. Independent white/black major piece choices. Limited or full promotion. 32 configurations.',
  chess960: 'Chess960 starting position (960 possibilities). Queens only. Limited promotion. 960 configurations.',
  chess960x2: 'Chess960 starting position (960 possibilities). Queens only. Full promotion. 1,920 configurations.',
  chess960x8: 'Chess960 starting position (960 possibilities). Both players use same major piece. Limited or full promotion. 7,680 configurations.',
  chess960x32: 'Chess960 starting position (960 possibilities). Independent white/black major piece choices. Limited or full promotion. 30,720 configurations.',
  chess960blind: 'Online-only. Each player secretly picks one of the 960 arrangements for their own first rank. One shared major piece for both players. The setups are revealed when the game starts.'
};

export const GameVariantSelector = ({
  gameVariation,
  setGameVariation,
  gameMode = 'online'
}: GameVariantSelectorProps) => {
  const getGroupForVariation = (variation: GameVariation): VariantGroup => {
    if (variation === 'chess960blind') {
      return 'blind';
    }
    if (variation.includes('chess960')) {
      return variation.includes('x8') || variation.includes('x32') ? 'custom' : 'chess960';
    } else {
      return variation.includes('x8') || variation.includes('x32') ? 'custom' : 'standard';
    }
  };

  const currentGroup = getGroupForVariation(gameVariation);

  return (
    <Card variant="glass" padding="lg">
      <h3 className="text-lg font-bold text-white mb-4">Game Variant</h3>
      <div className="space-y-4">
        {Object.entries(variantGroups).map(([groupId, group]) => {
          const isActive = groupId === currentGroup;
          return (
            <div key={groupId} className={`border-2 rounded-xl p-4 transition-all duration-200 ${
              isActive ? 'border-cyan-500 bg-cyan-500/10' : 'border-slate-700 bg-slate-800/30 hover:bg-slate-800/50'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-white">{group.label}</h4>
                {isActive && <Badge variant="primary">Selected</Badge>}
              </div>
              <p className="text-sm text-slate-300 mb-3">{group.description}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {group.variations.map(variation => {
                  const isOnlineOnlyDisabled = variation === 'chess960blind' && gameMode === 'local';
                  return (
                  <Tooltip 
                    key={variation}
                    content={isOnlineOnlyDisabled
                      ? 'Blind Chess960 is online-only: it needs two players who cannot see each other\'s screen. Switch to Online play to use it.'
                      : variationDescriptions[variation]}
                    position="top"
                    delay={200}
                    multiline={true}
                    maxWidth="max-w-xs sm:max-w-sm md:max-w-md"
                  >
                    <button
                      onClick={() => setGameVariation(variation)}
                      disabled={isOnlineOnlyDisabled}
                      className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                        gameVariation === variation
                          ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 text-white'
                          : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      } disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-slate-800`}
                    >
                      <span className="line-clamp-1">
                        {variationLabels[variation]}{isOnlineOnlyDisabled ? ' (online)' : ''}
                      </span>
                    </button>
                  </Tooltip>
                  );
                })}
              </div>
              {groupId === 'blind' && gameMode === 'local' && (
                <p className="mt-2 text-xs text-slate-400">
                  Blind Chess960 is only available in online games (lobby invites and private rooms).
                </p>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 text-xs text-slate-400">
        <strong>Tip:</strong> Start with <strong>Standard Chess</strong> for traditional play, <strong>Chess960</strong> for randomized openings, or <strong>Custom Major Pieces</strong> to replace queens with Hawk/Elephant/Amazon. <strong>Hover or tap any variant button for detailed explanation.</strong>
      </div>
    </Card>
  );
};